import React, { useEffect, useState } from 'react';
import CustomerSelect from '../../../CustomerSelect/Facelift';
import StatesSelect from '../../../StatesSelect/Facelift';
import { ILongSteelOrdersState } from '../../../../store/ducks/long-steel-orders/types';
import { ICustomer } from '../../../../store/ducks/customers/types';
import { InvoicesState } from '../../../../store/ducks/invoices/types';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import IsManagerCustomerInput from '../../../IsManagerCustomerInput';
import { uppercaseOnlyFirstLetter } from '../../../../utils/formatStrings';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';

interface ISoldToViewProps {
  orderOrInvoiceState: ILongSteelOrdersState | InvoicesState;
  customers: ICustomer[];
  isUserAdmin: boolean;
  emitterIds: string[];
  setEmitterIds(emitterIds: string[]): void;
  receiverId: string;
  setReceiverId(receiverId: string): void;

  showCustomerStates: boolean;
  selectedStates: string[];
  onChangeStates(states: string[]): void;
}

const getStatesFromCustomerArray = (
  customers: ICustomer[]
): TSelectOptionProps[] => {
  const states: TSelectOptionProps[] = [];
  customers.forEach((customer) => {
    if (!states.find((state) => state.value === customer.locality.state)) {
      states.push({
        label: customer.locality.state,
        value: customer.locality.state
      });
    }
  });

  states.sort((a, b) => {
    if (a.label < b.label) return -1;

    return a.label > b.label ? 1 : 0;
  });

  return states;
};

const getStatesOnlyString = (states: TSelectOptionProps[]): string[] => {
  return states.map((state) => state.value);
};

const SoldToView: React.FC<ISoldToViewProps> = ({
  orderOrInvoiceState,
  customers,
  isUserAdmin,
  showCustomerStates,
  selectedStates,
  onChangeStates,
  emitterIds,
  setEmitterIds
}) => {
  const customerState = useSelector(
    (appState: ApplicationState) => appState.customers
  );

  const [states, setStates] = useState<TSelectOptionProps[]>([]);

  useEffect(() => {
    if (
      showCustomerStates &&
      !customerState.loading &&
      customers.length > 0 &&
      states.length === 0
    ) {
      setStates(getStatesFromCustomerArray(customers));
    }
  }, [showCustomerStates, customerState.loading, customers, states.length]);

  useEffect(() => {
    if (states.length === 1 && selectedStates.length === 0) {
      const [state] = getStatesOnlyString(states);
      onChangeStates([state]);
    }
  });

  useEffect(() => {
    if (states.length === selectedStates.length) {
      setEmitterIds(customers.map((customer) => customer.customerId));
      return;
    }
    const emittersSelected: string[] = [];
    selectedStates.forEach((state) => {
      const filteredCustomers = customers.filter(
        (cust) => state === cust.locality.state
      );

      if (filteredCustomers.length === 1) {
        emittersSelected.push(filteredCustomers[0].customerId);
      }
    });

    setEmitterIds(emittersSelected);
  }, [selectedStates]);

  const getSelectedCustomer = (customerId: string) => {
    return customers.find((cust) => cust.customerId === customerId);
  };

  return (
    <>
      {isUserAdmin ? (
        <IsManagerCustomerInput
          title="Emissor do pedido"
          customerId={emitterIds.length > 0 ? emitterIds[0] : ''}
          setCustomerId={(value) => setEmitterIds([value])}
          loading={orderOrInvoiceState.loading}
          bpError={orderOrInvoiceState.bpError || false}
          customerName={
            !orderOrInvoiceState.loading &&
            customers.length > 0 &&
            emitterIds.length > 0 &&
            emitterIds[0].length > 6
              ? uppercaseOnlyFirstLetter(
                  getSelectedCustomer(emitterIds[0])?.name || '',
                  35
                )
              : undefined
          }
        />
      ) : showCustomerStates ? (
        <>
          <StatesSelect
            title={'Estado do emissor'}
            disabled={customerState.loading || orderOrInvoiceState.loading}
            states={states || []}
            selectedStates={selectedStates}
            onChangeState={onChangeStates}
            loading={customerState.loading}
          ></StatesSelect>

          <CustomerSelect
            title={'Emissor do pedido'}
            placeholder="Selecionar emissores"
            disabled={orderOrInvoiceState.loading || customerState.loading}
            customers={customers.filter((cust) =>
              selectedStates.includes(cust.locality.state)
            )}
            onSelectMultiple={(customerIds) => setEmitterIds(customerIds)}
            selectedCustomerIds={emitterIds}
            isMultipleSelect={true}
            customStyles={{ selectTitleFontSize: '0.8rem' }}
          ></CustomerSelect>
        </>
      ) : (
        <CustomerSelect
          title={'Emissor do pedido'}
          placeholder="Selecionar emissor"
          disabled={orderOrInvoiceState.loading || customerState.loading}
          customers={customers || []}
          onSelect={(customerId) => setEmitterIds([customerId])}
          selectedCustomerId={emitterIds.length ? emitterIds[0] : undefined}
          customStyles={{ selectTitleFontSize: '0.8rem' }}
        ></CustomerSelect>
      )}
    </>
  );
};

export default SoldToView;
